// Rescue chamber detail page. Two-column layout: occupants on the left,
// chamber details + location preview on the right (matches mobile parity).

import React, {useCallback, useEffect, useState} from 'react'
import clsx from 'clsx'
import {useNavigate, useParams} from 'react-router-dom'
import {Avatar} from '../../components/common/Avatar'
import {Header} from '../../components/Header'
import {PageShell} from '../../components/PageShell'
import {supabase} from '../../app/supabaseClient'
import {formatTimeAgo} from '../../lib/helpers'
import type {ChamberDetail, ChamberStatus, OccupantRow} from './types'

const STATUS_LABEL: Record<ChamberStatus, string> = {
  available: 'Available',
  full: 'Full',
  near_capacity: 'Near capacity',
  maintenance: 'Maintenance',
}

const STATUS_PILL_CLASS: Record<ChamberStatus, string> = {
  available:     'bg-badgeGreenBg  text-statusGreen',
  full:          'bg-badgeRedBg    text-statusRed',
  near_capacity: 'bg-badgeOrangeBg text-statusOrange',
  maintenance:   'bg-surfaceLight  text-textMuted',
}

interface PersonnelMini {
  id: string
  name: string
  employee_id: string
  designation: string | null
  photo: string | null
}

interface TagMini {
  id: string
  tag_id: string
  assigned_to_id: string | null
  assigned_to_type: string | null
  battery_level: number
  signal_strength: string
}

interface ChamberPersonnelRow {
  id: string
  chamber_id: string
  personnel_id: string
  entered_at: string
}

export const ChamberDetailScreen: React.FC = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const [chamber, setChamber] = useState<ChamberDetail | null>(null)
  const [occupants, setOccupants] = useState<OccupantRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Extracted into a callback so a realtime chamber_personnel event can
  // refetch the occupants list without re-running the chamber fetch.
  const loadOccupants = useCallback(async () => {
    if (!id) return
    const {data: cp} = await supabase
      .from('chamber_personnel')
      .select('id, chamber_id, personnel_id, entered_at')
      .eq('chamber_id', id)
    const cpRows = (cp ?? []) as ChamberPersonnelRow[]
    const pids = cpRows.map(r => r.personnel_id)

    const [{data: people}, {data: tags}] = await Promise.all([
      pids.length
        ? supabase
            .from('personnel')
            .select('id, name, employee_id, designation, photo')
            .in('id', pids)
        : Promise.resolve({data: [] as PersonnelMini[]}),
      pids.length
        ? supabase
            .from('tags')
            .select('id, tag_id, assigned_to_id, assigned_to_type, battery_level, signal_strength')
            .eq('assigned_to_type', 'personnel')
            .in('assigned_to_id', pids)
        : Promise.resolve({data: [] as TagMini[]}),
    ])

    const personById = new Map((people ?? []).map(p => [p.id, p as PersonnelMini]))
    const tagByPerson = new Map<string, TagMini>()
    for (const t of (tags ?? []) as TagMini[]) {
      if (t.assigned_to_id && !tagByPerson.has(t.assigned_to_id)) {
        tagByPerson.set(t.assigned_to_id, t)
      }
    }
    const rows: OccupantRow[] = []
    for (const r of cpRows) {
      const p = personById.get(r.personnel_id)
      if (!p) continue
      const tag = tagByPerson.get(p.id)
      rows.push({
        id: r.id,
        personnel_id: p.id,
        personnel_name: p.name,
        employee_id: p.employee_id,
        designation: p.designation,
        photo: p.photo,
        entered_at: r.entered_at,
        primary_tag_id: tag?.tag_id ?? null,
        battery_level: tag?.battery_level ?? null,
        signal_strength: tag?.signal_strength ?? null,
      })
    }
    setOccupants(rows)
  }, [id])

  // Initial fetch — chamber row + occupants.
  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const {data: c, error: ce} = await supabase
        .from('rescue_chambers')
        .select(
          'id, name, location, location_description, capacity, current_occupancy, ' +
            'status, maintenance_status, last_inspection_date, installation_date, access_point, ' +
            'x, y, z, level',
        )
        .eq('id', id)
        .single<ChamberDetail>()
      if (cancelled) return
      if (ce || !c) {
        setError(ce?.message ?? 'Not found')
        setLoading(false)
        return
      }
      setChamber(c)
      await loadOccupants()
      if (cancelled) return
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id, loadOccupants])

  // Live updates: chamber_personnel scoped to this chamber. On INSERT or
  // DELETE we refetch occupants (cheaper than maintaining incremental join
  // state in the component).
  useEffect(() => {
    if (!id) return
    const channel = supabase
      .channel(`chamber-${id}-personnel`)
      .on(
        'postgres_changes' as any,
        {event: '*', schema: 'public', table: 'chamber_personnel', filter: `chamber_id=eq.${id}`},
        () => {
          void loadOccupants()
        },
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }, [id, loadOccupants])

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <Header
        title={chamber ? `Rescue Chamber : ${chamber.name}` : 'Chamber'}
        backTo="/chambers"
      />
      <PageShell
        footer={
          chamber && (
            <button
              type="button"
              onClick={() => navigate(`/chambers/${chamber.id}/edit`)}
              className="rounded-md border border-primary px-lg py-sm text-md font-semibold text-primary hover:bg-primaryBg"
            >
              Edit
            </button>
          )
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-textMuted">Loading…</div>
        ) : error || !chamber ? (
          <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
            <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
            <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">{error ?? 'Not found'}</pre>
          </div>
        ) : (
          <div className="flex flex-col gap-lg lg:flex-row">
            <div className="min-w-0 flex-1">
              <OccupantsList occupants={occupants} />
            </div>
            <aside className="flex w-full shrink-0 flex-col gap-lg lg:w-[360px]">
              <ChamberDetailsCard
                c={chamber}
                onUpdate={() => navigate(`/chambers/${chamber.id}/edit`)}
              />
              <LocationPreviewCard
                c={chamber}
                onUpdate={() => navigate(`/chambers/${chamber.id}/edit`)}
              />
            </aside>
          </div>
        )}
      </PageShell>
    </main>
  )
}

const ChamberDetailsCard: React.FC<{c: ChamberDetail; onUpdate: () => void}> = ({c, onUpdate}) => (
  <div className="rounded-lg border border-surfaceBorder bg-surfaceCard p-lg">
    <div className="mb-md flex items-center justify-between">
      <h3 className="text-lg font-bold text-textPrimary">Chamber Details</h3>
      <button
        type="button"
        onClick={onUpdate}
        className="rounded-md border border-primary px-md py-xs text-sm font-semibold text-primary hover:bg-primaryBg"
      >
        Update
      </button>
    </div>
    <dl className="grid grid-cols-2 gap-x-lg gap-y-md">
      <Field label="Capacity" value={`${c.current_occupancy} / ${c.capacity}`} />
      <Field label="Status" value={STATUS_LABEL[c.status]} valueClass={STATUS_PILL_CLASS[c.status].split(' ')[1]} />
      <Field label="Access Point" value={c.access_point ?? '—'} />
      <Field
        label="Coordinates"
        value={
          c.x != null && c.y != null && c.z != null
            ? `X: ${c.x.toFixed(1)} | Y: ${c.y.toFixed(1)} | Z: ${c.z.toFixed(1)}`
            : '—'
        }
      />
      <div className="col-span-2">
        <Field label="Location Description" value={c.location_description ?? '—'} />
      </div>
      <Field label="Maintenance Status" value={c.maintenance_status ?? '—'} />
      <Field
        label="Last Inspection"
        value={c.last_inspection_date ? new Date(c.last_inspection_date).toLocaleDateString() : '—'}
      />
      <div className="col-span-2">
        <Field
          label="Installation Date"
          value={c.installation_date ? new Date(c.installation_date).toLocaleDateString() : '—'}
        />
      </div>
    </dl>
  </div>
)

const LocationPreviewCard: React.FC<{c: ChamberDetail; onUpdate: () => void}> = ({c, onUpdate}) => (
  <div className="rounded-lg border border-surfaceBorder bg-surfaceCard p-lg">
    <div className="mb-md flex items-center justify-between">
      <h3 className="text-lg font-bold text-textPrimary">Location Preview</h3>
      <button
        type="button"
        onClick={onUpdate}
        className="rounded-md border border-primary px-md py-xs text-sm font-semibold text-primary hover:bg-primaryBg"
      >
        Update
      </button>
    </div>
    <div className="flex flex-col items-center justify-center gap-md py-lg">
      <div className="text-md font-bold text-primary">{c.name}</div>
      <div className="h-px w-full bg-primary" />
      <div className="flex items-center gap-sm">
        <span className="h-2 w-2 rounded-full bg-primary" />
        <span className="text-xs uppercase tracking-wider text-textMuted">
          {c.location_description || c.location}
        </span>
      </div>
    </div>
  </div>
)

const Field: React.FC<{label: string; value: string; valueClass?: string}> = ({label, value, valueClass}) => (
  <div>
    <dt className="text-xs uppercase tracking-wider text-textMuted">{label}</dt>
    <dd className={clsx('mt-1 text-md text-textPrimary', valueClass)}>{value}</dd>
  </div>
)

const OccupantsList: React.FC<{occupants: OccupantRow[]}> = ({occupants}) => (
  <div className="rounded-lg border border-surfaceBorder bg-surfaceCard p-lg">
    <div className="mb-lg flex items-center justify-between">
      <h3 className="text-lg font-bold text-textPrimary">Personnel in Chamber</h3>
      <span className="text-sm text-textMuted">{occupants.length} people</span>
    </div>
    {occupants.length === 0 ? (
      <div className="text-sm text-textMuted">No personnel currently in this chamber.</div>
    ) : (
      <ul className="flex flex-col gap-sm">
        {occupants.map(o => (
          <li
            key={o.id}
            className="flex items-center gap-md rounded-md border border-surfaceBorder bg-surface px-lg py-md"
          >
            <Avatar uri={o.photo} name={o.personnel_name} size={40} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-md font-semibold text-textPrimary">
                {o.personnel_name}
              </div>
              <div className="truncate text-xs text-textMuted">
                {o.employee_id}
                {o.designation && <> · {o.designation}</>}
              </div>
              <div className="mt-xs flex items-center gap-md text-xs">
                {o.primary_tag_id ? (
                  <span className="text-statusGreen">#{o.primary_tag_id}</span>
                ) : (
                  <span className="text-textMuted">No Tag</span>
                )}
                <span className="text-textMuted">
                  {o.battery_level != null ? `${o.battery_level}%` : 'N/A'}
                </span>
                <span className="text-textMuted">{o.signal_strength ?? 'N/A'}</span>
              </div>
            </div>
            <span className="shrink-0 text-xs text-textMuted">{formatTimeAgo(o.entered_at)}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
)
